import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../constants/api';

// Patient/sales data, sourced from our own backend (proxies the
// IT Dose dump in s3://trustlab-fyear-data). Response shape:
//   { status, message, meta, filters, data: [...serviceRows] }
//
// One page at a time — the component below (PatientReports) drives the
// `page` arg from clickable pagination controls. Each page is cached
// separately by its `page` arg, so jumping back to a previously-viewed
// page renders instantly from cache instead of refetching.
//
// `keepUnusedDataFor: 3600` = 1 hour cache per page.

// Builds the query string for both endpoints.
//
// search / test / status / fromDate / toDate are applied SERVER-SIDE, across
// the customer's whole history, then the result is paginated. They used to be
// filtered in the browser over the 20 rows on screen, which meant a patient
// on page 400 could not be found at all.
//
// Empty values are omitted rather than sent blank: an absent parameter means
// "no filter" to the API, and it also keeps RTK Query's cache keys stable, so
// clearing a filter returns to the already-cached unfiltered page.
const reportQuery = (params) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || String(v).trim() === '') continue;
    qs.set(k, String(v).trim());
  }
  return qs.toString();
};

export const salesApi = createApi({
  reducerPath: 'salesApi',
  baseQuery: fetchBaseQuery({ baseUrl: API_BASE_URL }),
  keepUnusedDataFor: 3600,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    // Centre / hospital-wide view — every visit billed against a client.
    // GET /api/portal/patient-reports/by-customer?customerId=...&page=N&pageSize=20
    getSalesByCustomer: builder.query({
      query: ({ customerId, page = 1, pageSize = 20, ...filters }) =>
        `/portal/patient-reports/by-customer?${reportQuery({ customerId, page, pageSize, ...filters })}`,
    }),
    // Doctor-scoped view — same client, narrowed to visits referred
    // by one DoctorID. Used by the doctor portal so a doctor only
    // sees the rows they're attached to instead of every centre visit.
    // GET /api/portal/patient-reports/by-customer-doctor?customerId=...&doctorId=N&page=&pageSize=
    getSalesByCustomerAndDoctor: builder.query({
      query: ({ customerId, doctorId, page = 1, pageSize = 20, ...filters }) =>
        `/portal/patient-reports/by-customer-doctor?${reportQuery({ customerId, doctorId, page, pageSize, ...filters })}`,
    }),
  }),
});

export const {
  useGetSalesByCustomerQuery,
  useGetSalesByCustomerAndDoctorQuery,
  // Lazy variants — used by Export, which pages through the whole filtered
  // result set on demand rather than subscribing to it.
  useLazyGetSalesByCustomerQuery,
  useLazyGetSalesByCustomerAndDoctorQuery,
} = salesApi;
